import { useContext, useEffect } from 'react';

import { SummaryGrid } from '~/components/modules/SummaryTable/SummaryGrid';
import { AnalyticsEventNames } from '~/constants/AnalyticsEventMap';
import { DepositContext } from '~/context/Deposit/DepositContext';
import { useGetWireInformation } from '~/hooks/payments/useGetWireInformation';
import { useTracking } from '~/hooks/tracking/useTracking';
import { ModalMultiStepProps } from '~/models/deposits/ModalMultiStepProps';
import { Button } from '~/vinovest-design-system/components';

import { PortfolioDestination } from './PortfolioDestination';
import { WireTransferEntry } from './WireTransferEntry';

interface DomesticWireTransferInstructionsProps extends ModalMultiStepProps {
    onView?: () => void;
}

/** Instructions for making a US wire transfer. */
export const DomesticWireTransferInstructions = ({ setStep, onView }: DomesticWireTransferInstructionsProps) => {
    const { captureEvent } = useTracking();
    const { destinationPortfolioId, destinationPortfolio, setDestinationPortfolio } = useContext(DepositContext);
    const { data: wireInformation, isLoading } = useGetWireInformation(destinationPortfolioId);

    const wireEntries = [
        {
            label: 'Bank Name',
            value: wireInformation?.bankName
        },
        { label: 'ACH Routing Number', value: wireInformation?.routingNumber },
        { label: 'Wire Routing Number', value: wireInformation?.wireRoutingNumber },
        { label: 'Receiving Bank / Beneficiary Bank Address', value: wireInformation?.bankAddress },
        { label: 'Beneficiary Name', value: wireInformation?.beneficiary },
        { label: 'Account Number', value: wireInformation?.accountNumber }
    ];

    useEffect(() => {
        if (onView != null) {
            onView();
        }
    }, []);

    return (
        <SummaryGrid>
            <WireTransferEntry label={'Deposit to'} classnames={'print:hidden'}>
                <PortfolioDestination destination={destinationPortfolio} setDestination={setDestinationPortfolio} />
            </WireTransferEntry>
            {wireEntries.map(({ label, value }) => (
                <>
                    {Boolean(value) && (
                        <WireTransferEntry key={`domestic-wine-${label}`} label={label} renderCondition={!isLoading}>
                            {value}
                        </WireTransferEntry>
                    )}
                </>
            ))}
            <div className={'flex flex-col py-3 px-4 sm:px-6 print:hidden'}>
                <Button
                    variant={'platinum'}
                    classnames={'mb-5'}
                    onClick={() => {
                        window.print();
                        captureEvent(AnalyticsEventNames.DownloadWirePDF, {
                            type: 'domestic',
                            portfolio: destinationPortfolio
                        });
                    }}
                >
                    Print Instructions
                </Button>
                <Button variant={'secondary'} onClick={() => setStep(0)}>
                    Back
                </Button>
            </div>
        </SummaryGrid>
    );
};
